import React, { useState } from 'react';
import type { WorkshopProject, NodePotential } from '../../model/types';
import { useWorkshopStore } from '../../store/workshopStore';
import {
  createTopWireDuct,
  createMidWireDuct,
  createLeftWireDuct,
  createRightWireDuct,
  createMcb3P,
  createMcb1P,
  createContactor3P,
  createThermalRelay,
  createMotor3Phase,
  createStopButton,
  createStartButton,
  createPilotLamp,
} from '../../model/catalog';
import type { ElectricalDevice } from '../../model/types';

export interface WorkshopToolboxProps {
  project?: WorkshopProject;
  activeTerminalId?: string | null;
  nodePotentials?: Record<string, NodePotential>;
}

export interface PaletteItem {
  id: string;
  category: 'Kablo Kanalları' | 'Sigortalar & Kontaktör' | 'Motor & Yük' | 'Pano Kapağı Donanımları';
  designator: string;
  name: string;
  spec: string;
  icon: string;
  factory: () => ElectricalDevice;
}

export const PALETTE_ITEMS: PaletteItem[] = [
  // 1. Kablo Kanalları
  {
    id: 'duct_top',
    category: 'Kablo Kanalları',
    designator: 'W-TOP',
    name: 'Üst Yatay Kablo Kanalı',
    spec: '504x34 mm Delikli PVC',
    icon: '📏',
    factory: createTopWireDuct,
  },
  {
    id: 'duct_mid',
    category: 'Kablo Kanalları',
    designator: 'W-MID',
    name: 'Orta Yatay Kablo Kanalı',
    spec: '504x34 mm Delikli PVC',
    icon: '📏',
    factory: createMidWireDuct,
  },
  {
    id: 'duct_left',
    category: 'Kablo Kanalları',
    designator: 'W-LEFT',
    name: 'Sol Dikey Kablo Kanalı',
    spec: '34x324 mm Delikli PVC',
    icon: '📐',
    factory: createLeftWireDuct,
  },
  {
    id: 'duct_right',
    category: 'Kablo Kanalları',
    designator: 'W-RIGHT',
    name: 'Sağ Dikey Kablo Kanalı',
    spec: '34x324 mm Delikli PVC',
    icon: '📐',
    factory: createRightWireDuct,
  },

  // 2. Sigortalar & Kontaktör
  {
    id: 'f1',
    category: 'Sigortalar & Kontaktör',
    designator: 'F1',
    name: '3 Kutuplu Güç Sigortası',
    spec: '3P 16A C Tipi MCB',
    icon: '🔴',
    factory: () => createMcb3P('f1', 'F1', 100, 195),
  },
  {
    id: 'f2',
    category: 'Sigortalar & Kontaktör',
    designator: 'F2',
    name: '1 Kutuplu Kumanda Sigortası',
    spec: '1P 6A B Tipi MCB',
    icon: '🔵',
    factory: () => createMcb1P('f2', 'F2', 196, 195),
  },
  {
    id: 'km1',
    category: 'Sigortalar & Kontaktör',
    designator: 'KM1',
    name: '3 Kutuplu Güç Kontaktörü',
    spec: 'AC-3 9A 230V Bobin, 1 NO',
    icon: '⚙️',
    factory: () => createContactor3P('km1', 'KM1', 244, 190),
  },
  {
    id: 'fr1',
    category: 'Sigortalar & Kontaktör',
    designator: 'FR1',
    name: 'Termik Aşırı Akım Rölesi',
    spec: '4.0 - 6.0A Bimetal',
    icon: '🌡️',
    factory: () => createThermalRelay('fr1', 'FR1', 244, 305),
  },

  // 3. Motor & Yük
  {
    id: 'm1',
    category: 'Motor & Yük',
    designator: 'M1',
    name: '3 Fazlı Asenkron Motor',
    spec: '1.5 kW 400V~ 1420 d/d',
    icon: '🌀',
    factory: () => createMotor3Phase('m1', 'M1', 160, 530),
  },

  // 4. Pano Kapağı Donanımları
  {
    id: 'sb1',
    category: 'Pano Kapağı Donanımları',
    designator: 'SB1',
    name: 'Stop Butonu',
    spec: '22mm Kırmızı Yaylı (1-2 NC)',
    icon: '🛑',
    factory: () => createStopButton('sb1', 'SB1', 480, 160),
  },
  {
    id: 'sb2',
    category: 'Pano Kapağı Donanımları',
    designator: 'SB2',
    name: 'Start Butonu',
    spec: '22mm Yeşil Yaylı (3-4 NO)',
    icon: '🟢',
    factory: () => createStartButton('sb2', 'SB2', 480, 240),
  },
  {
    id: 'hl1',
    category: 'Pano Kapağı Donanımları',
    designator: 'HL1',
    name: 'Çalışma Sinyal Lambası',
    spec: '22mm Yeşil LED (230V AC)',
    icon: '💡',
    factory: () => createPilotLamp('hl1', 'HL1', '#22c55e', 480, 320),
  },
  {
    id: 'hl2',
    category: 'Pano Kapağı Donanımları',
    designator: 'HL2',
    name: 'Arıza Sinyal Lambası',
    spec: '22mm Kırmızı LED (230V AC)',
    icon: '🚨',
    factory: () => createPilotLamp('hl2', 'HL2', '#ef4444', 480, 390),
  },
];

interface ComponentDoc {
  id: string;
  designator: string;
  name: string;
  spec: string;
  terminals: string[];
  description: string;
}

const COMPONENT_DOCS: ComponentDoc[] = [
  {
    id: 'f1',
    designator: 'F1',
    name: '3 Kutuplu Güç Sigortası',
    spec: '3P, 16A, C Tipi, 400V~ / 6kA',
    terminals: ['1, 3, 5 (Giriş Fazları)', '2, 4, 6 (Çıkış Fazları)'],
    description: 'Motor güç devresini aşırı akım ve kısa devrelere karşı korur.',
  },
  {
    id: 'f2',
    designator: 'F2',
    name: '1 Kutuplu Kumanda Sigortası',
    spec: '1P, 6A, B Tipi, 230V~ / 6kA',
    terminals: ['1 (Giriş Fazı)', '2 (Kumanda Çıkışı)'],
    description: 'Kumanda devresini, kontaktör bobinini ve buton hatlarını korur.',
  },
  {
    id: 'km1',
    designator: 'KM1',
    name: '3 Kutuplu Güç Kontaktörü',
    spec: 'AC-3 9A, 230V~ 50Hz Bobin, 1 NO',
    terminals: ['A1-A2 (Bobin)', '1-2, 3-4, 5-6 (Ana Kontak)', '13-14 (NO Mühürleme)'],
    description: 'Bobinine enerji verildiğinde ana ve yardımcı kontaklarını kapatarak motoru çalıştırır.',
  },
  {
    id: 'fr1',
    designator: 'FR1',
    name: 'Termik Aşırı Akım Rölesi',
    spec: 'Ayar Sahası: 4.0 - 6.0A, Bimetal',
    terminals: ['1-2, 3-4, 5-6 (Akım Yolu)', '95-96 (NC Devre Kesici)', '97-98 (NO Arıza İhbar)'],
    description: 'Motor aşırı yüklendiğinde ısınan bimetaller 95-96 kontağını açarak devreyi durdurur.',
  },
  {
    id: 'sb1',
    designator: 'SB1',
    name: 'Stop Butonu',
    spec: 'Kırmızı Yaylı Buton, 1 NC Kontak',
    terminals: ['1-2 (Normalde Kapalı NC)'],
    description: 'Basıldığında kumanda devresinin enerjisini keserek mühürlemeyi bozar.',
  },
  {
    id: 'sb2',
    designator: 'SB2',
    name: 'Start Butonu',
    spec: 'Yeşil Yaylı Buton, 1 NO Kontak',
    terminals: ['3-4 (Normalde Açık NO)'],
    description: 'Basıldığında kontaktör bobinine enerji vererek devreyi başlatır.',
  },
  {
    id: 'hl1',
    designator: 'HL1',
    name: 'Çalışma Sinyal Lambası',
    spec: 'Yeşil LED, 230V~ AC',
    terminals: ['X1 (+Faz Girişi)', 'X2 (Nötr Dönüşü)'],
    description: 'Kontaktör çektiğinde motorun devrede olduğunu gösterir.',
  },
  {
    id: 'hl2',
    designator: 'HL2',
    name: 'Arıza Sinyal Lambası',
    spec: 'Kırmızı LED, 230V~ AC',
    terminals: ['X1 (+Faz Girişi)', 'X2 (Nötr Dönüşü)'],
    description: 'Termik röle attığında (97-98 kapandığında) arıza ihbarı verir.',
  },
  {
    id: 'm1',
    designator: 'M1',
    name: '3 Fazlı Asenkron Motor',
    spec: '1.5 kW, 1420 d/d, 400V~ / 50Hz',
    terminals: ['U1, V1, W1 (3 Faz Sargı Uçları)', 'PE (Gövde Topraklaması)'],
    description: 'Kontaktör ve termik üzerinden gelen 3 faz enerjisiyle dönen tahrik motoru.',
  },
];

export const WorkshopToolbox: React.FC<WorkshopToolboxProps> = ({
  activeTerminalId,
  nodePotentials = {},
}) => {
  const [activeTab, setActiveTab] = useState<'palette' | 'multimeter' | 'docs'>('palette');
  const [selectedCompId, setSelectedCompId] = useState<string>('km1');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const project = useWorkshopStore((s) => s.project);
  const selectedPaletteItemId = useWorkshopStore((s) => s.selectedPaletteItemId);
  const setSelectedPaletteItem = useWorkshopStore((s) => s.setSelectedPaletteItem);
  const addDevice = useWorkshopStore((s) => s.addDevice);
  const removeDevice = useWorkshopStore((s) => s.removeDevice);

  const selectedDoc = COMPONENT_DOCS.find((c) => c.id === selectedCompId) || COMPONENT_DOCS[0]!;

  // Aktif klemens voltajını hesapla
  let activeVoltageText = 'Klemens seçilmedi';
  let activeVoltageColor = '#64748b';
  if (activeTerminalId) {
    const pot = nodePotentials[activeTerminalId];
    if (pot === 'L1') {
      activeVoltageText = '230V~ AC (L1 Fazı, Canlı)';
      activeVoltageColor = '#f59e0b';
    } else if (pot === 'L2') {
      activeVoltageText = '230V~ AC (L2 Fazı, Canlı)';
      activeVoltageColor = '#ea580c';
    } else if (pot === 'L3') {
      activeVoltageText = '230V~ AC (L3 Fazı, Canlı)';
      activeVoltageColor = '#cbd5e1';
    } else if (pot === 'N') {
      activeVoltageText = '0V~ AC (Nötr Dönüş Hattı)';
      activeVoltageColor = '#38bdf8';
    } else if (pot === 'PE') {
      activeVoltageText = '0V (Koruma Topraklaması)';
      activeVoltageColor = '#84cc16';
    } else {
      activeVoltageText = '0V (Açık Devre / Gerilim Yok)';
      activeVoltageColor = '#94a3b8';
    }
  }

  if (isCollapsed) {
    return (
      <aside
        style={{
          width: 38,
          height: '100%',
          background: '#111827',
          borderRight: '1px solid #1f293d',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '12px 4px',
          cursor: 'pointer',
        }}
        onClick={() => setIsCollapsed(false)}
        title="Eleman Kütüphanesini Göster"
      >
        <span style={{ writingMode: 'vertical-rl', color: '#38bdf8', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
          ▶ ELEMANLAR & PALET
        </span>
      </aside>
    );
  }

  return (
    <aside
      style={{
        width: 290,
        height: '100%',
        background: '#111827',
        borderRight: '1px solid #1f293d',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontSize: 12,
        color: '#cbd5e1',
        zIndex: 5,
      }}
    >
      {/* Başlık ve Kapatma Butonu */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 14px',
          background: '#162235',
          borderBottom: '1px solid #233148',
        }}
      >
        <span style={{ fontWeight: 700, color: '#38bdf8', fontSize: 12.5 }}>
          📐 ATÖLYE ÇEKMECESİ
        </span>
        <button
          type="button"
          onClick={() => setIsCollapsed(true)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: 14,
            padding: '2px 6px',
          }}
          title="Paneli Daralt"
        >
          ◀
        </button>
      </div>

      {/* Sekme Seçici (Palet / Multimetre / Bilgi) */}
      <div
        style={{
          display: 'flex',
          background: '#0d131f',
          borderBottom: '1px solid #1f293d',
          padding: '4px',
          gap: 4,
        }}
      >
        {[
          { id: 'palette', label: '🧩 Komponentler' },
          { id: 'multimeter', label: '⚡ Multimetre' },
          { id: 'docs', label: 'ℹ️ Bilgi' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id as 'palette' | 'multimeter' | 'docs')}
            style={{
              flex: 1,
              padding: '6px 4px',
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 4,
              border: 'none',
              background: activeTab === tab.id ? '#0284c7' : 'transparent',
              color: activeTab === tab.id ? '#ffffff' : '#94a3b8',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* SEKME 1: KOMPONENT SEÇİM VE YERLEŞTİRME PALETİ */}
      {activeTab === 'palette' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Yardımcı Talimat Bilgisi */}
          <div
            style={{
              padding: '8px 10px',
              background: '#0b1320',
              border: '1px solid #1e293b',
              borderRadius: 6,
              fontSize: 11,
              color: '#94a3b8',
              lineHeight: 1.4,
            }}
          >
            {selectedPaletteItemId ? (
              <span style={{ color: '#38bdf8', fontWeight: 600 }}>
                👉 Seçildi: Panodaki parlayan yuvaya tıklayarak monte edin!
              </span>
            ) : (
              <span>
                Elemana tıklayarak seçin, panodaki boş yuvaya tıklayarak monte edin.
              </span>
            )}
          </div>

          {/* Kategorilerine Göre Eleman Listesi */}
          {(['Kablo Kanalları', 'Sigortalar & Kontaktör', 'Motor & Yük', 'Pano Kapağı Donanımları'] as const).map(
            (category) => {
              const items = PALETTE_ITEMS.filter((i) => i.category === category);
              return (
                <div key={category} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#38bdf8', paddingBottom: 2, borderBottom: '1px solid #1e293b' }}>
                    {category}
                  </div>
                  {items.map((item) => {
                    const isPlaced = project.devices.some((d) => d.id === item.id);
                    const isSelected = selectedPaletteItemId === item.id;

                    return (
                      <div
                        key={item.id}
                        onClick={() => {
                          if (isPlaced) {
                            setSelectedPaletteItem(null);
                          } else {
                            setSelectedPaletteItem(isSelected ? null : item.id);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 9px',
                          borderRadius: 6,
                          background: isSelected ? '#1e2d44' : isPlaced ? '#131b28' : '#141c2c',
                          border: isSelected
                            ? '1.5px solid #38bdf8'
                            : isPlaced
                            ? '1px solid #1e3a5f'
                            : '1px solid #233148',
                          cursor: isPlaced ? 'default' : 'pointer',
                          transition: 'all 0.15s ease',
                          boxShadow: isSelected ? '0 0 10px rgba(56, 189, 248, 0.25)' : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                          <span style={{ fontSize: 16 }}>{item.icon}</span>
                          <div style={{ overflow: 'hidden' }}>
                            <div style={{ fontWeight: 700, fontSize: 11.5, color: isSelected ? '#38bdf8' : isPlaced ? '#94a3b8' : '#f1f5f9' }}>
                              {item.designator} — {item.name}
                            </div>
                            <div style={{ fontSize: 10, color: '#64748b' }}>{item.spec}</div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {isPlaced ? (
                            <>
                              <span style={{ fontSize: 9.5, color: '#10b981', fontWeight: 600 }}>
                                ✓ Panoda
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeDevice(item.id);
                                }}
                                style={{
                                  background: '#dc2626',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: 4,
                                  padding: '3px 6px',
                                  fontSize: 10,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                                title="Bu elemanı panodan sök"
                              >
                                Sil
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                addDevice(item.factory());
                              }}
                              style={{
                                background: isSelected ? '#0284c7' : '#1e293b',
                                color: isSelected ? '#ffffff' : '#38bdf8',
                                border: '1px solid #38bdf8',
                                borderRadius: 4,
                                padding: '3px 8px',
                                fontSize: 10.5,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                              title="Doğrudan panoya monte et"
                            >
                              {isSelected ? 'Seçili' : '+ Ekle'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
          )}
        </div>
      )}

      {/* SEKME 2: CANLI MULTİMETRE / VOLTMETRE */}
      {activeTab === 'multimeter' && (
        <div style={{ padding: '12px' }}>
          <div
            style={{
              padding: '12px',
              background: '#090d16',
              borderRadius: 8,
              border: '1px solid #243247',
              marginBottom: 10,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>
                ⚡ CANLI VOLTMETRE PROBU
              </span>
              <span style={{ fontSize: 11, color: '#38bdf8', fontFamily: 'monospace' }}>
                {activeTerminalId ? activeTerminalId.toUpperCase() : '---'}
              </span>
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 'bold',
                color: activeVoltageColor,
                fontFamily: 'monospace',
                background: '#04070d',
                padding: '8px 10px',
                borderRadius: 4,
                border: '1px solid #172336',
              }}
            >
              {activeVoltageText}
            </div>
            <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>
              {activeTerminalId
                ? 'Terminal seçili durumda. Başka bir klemense tıklayarak potansiyel ölçün.'
                : 'Ölçüm için panodaki veya şemadaki herhangi bir klemense tıklayın.'}
            </div>
          </div>
        </div>
      )}

      {/* SEKME 3: TEKNİK BİLGİ & DÖKÜMANTASYON */}
      {activeTab === 'docs' && (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>
            ELEMAN KATALOĞU
          </div>
          <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {COMPONENT_DOCS.map((doc) => {
              const isSelected = doc.id === selectedCompId;
              return (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => setSelectedCompId(doc.id)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 10px',
                    borderRadius: 6,
                    background: isSelected ? '#1e2d44' : '#141c2c',
                    border: isSelected ? '1px solid #38bdf8' : '1px solid #1e283b',
                    color: isSelected ? '#ffffff' : '#94a3b8',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 700, color: isSelected ? '#38bdf8' : '#e2e8f0', marginRight: 6 }}>
                      {doc.designator}
                    </span>
                    <span style={{ fontSize: 11 }}>{doc.name}</span>
                  </div>
                  <span style={{ fontSize: 10, color: '#64748b' }}>ℹ️</span>
                </button>
              );
            })}
          </div>

          <div
            style={{
              padding: '12px',
              background: '#0d131f',
              borderTop: '1px solid #1f293d',
              marginTop: 10,
              fontSize: 11,
            }}
          >
            <div style={{ fontWeight: 700, color: '#38bdf8', marginBottom: 2 }}>
              {selectedDoc.designator} — {selectedDoc.name}
            </div>
            <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 6, fontFamily: 'monospace' }}>
              {selectedDoc.spec}
            </div>
            <div style={{ color: '#cbd5e1', lineHeight: 1.4, marginBottom: 6 }}>
              {selectedDoc.description}
            </div>
            <div style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: 2 }}>
              Klemensler:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedDoc.terminals.map((t, idx) => (
                <div key={idx} style={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                  • {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};
